/**
 * EU driving-licence front parser, keyed on the HARMONISED FIELD NUMBERS, not
 * on language, so one parser reads IT, DE, FR, ES, NL, AT and UK licences (the
 * numbering is the same across them, 2006/126/EC Annex I):
 *
 *   1  surname        4a issue date      5  licence number
 *   2  given names    4b expiry date     9  categories
 *   3  date & place   4c issuing
 *      of birth          authority
 *
 * Pure: it takes recognised lines and returns the fields it is confident about.
 * OCR is messy — lines arrive out of order, the small field labels blur, and
 * O/0 and I/1 swap inside dates — so the matching is per line by its leading
 * field number, and dates get a light digit clean-up before they are read.
 */

import type { ScanField, ScannedIdentity } from '../ocr-interface'
import type { MrzInput } from './mrz'

/** A line's leading field number (4a/4b/4c or 1,2,3,5,9) and the value after it. */
const FIELD_RE = /^\s*(4\s*[abc]|[12359])\s*[.)·:-]?\s+(.+)$/i

/** A DD.MM.YYYY date anywhere in a string; separators vary and OCR adds noise. */
const DATE_RE = /(\d{1,2})\s*[.\-/]\s*(\d{1,2})\s*[.\-/]\s*(\d{2,4})/

/** In a run of digits, fold the usual OCR letter confusions back to numbers. */
function digitsOf(s: string): string {
  return s
    .replace(/[OoDQ]/g, '0')
    .replace(/[IilL|]/g, '1')
    .replace(/[Ss]/g, '5')
    .replace(/[Bb]/g, '8')
}

/**
 * "01.03.2015" (DD.MM.YYYY, EU) → ISO, or null when it is not a real date. A
 * licence prints four-digit years; OCR may keep two. Those are read by what
 * the field is (OPS-1): a date that lies AHEAD (the expiry, 4b) is always this
 * century — "28" is 2028, never 1928 — and one that lies BEHIND (birth, issue)
 * is this century up to this year, the last one past it.
 */
export function parseEuDate(raw: string, lies: 'behind' | 'ahead' = 'behind'): string | null {
  const m = DATE_RE.exec(raw.replace(/[OoIilLSsBb]/g, (c) => digitsOf(c)))
  if (!m) return null
  const dd = Number(m[1])
  const mm = Number(m[2])
  let yyyy = Number(m[3])
  if (m[3]?.length === 2) {
    const thisCentury = lies === 'ahead' || yyyy <= new Date().getUTCFullYear() % 100
    yyyy += thisCentury ? 2000 : 1900
  }
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1900 || yyyy > 2100) return null
  const iso = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
  const d = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(d.getTime()) || d.getUTCDate() !== dd) return null
  return iso
}

interface Marked {
  readonly value: string
  readonly confidence: number
}

/** Gather each field's value by its leading number; first/best read per field. */
function markedFields(input: MrzInput): Map<string, Marked> {
  const lines =
    typeof input === 'string'
      ? input.split(/\r?\n/).map((text) => ({ text, confidence: 0.7 }))
      : input.map((l) => (typeof l === 'string' ? { text: l, confidence: 0.7 } : l))
  const out = new Map<string, Marked>()
  for (const line of lines) {
    const m = FIELD_RE.exec(line.text)
    if (!m) continue
    const key = (m[1] ?? '').toLowerCase().replace(/\s+/g, '')
    const value = (m[2] ?? '').trim()
    const prev = out.get(key)
    if (!prev || line.confidence > prev.confidence) {
      out.set(key, { value, confidence: line.confidence })
    }
  }
  return out
}

function field<T>(value: T, confidence: number): ScanField<T> {
  return { value, confidence, source: 'licence' }
}

/**
 * Letters, spaces, apostrophes and hyphens only — a name, not its label noise.
 * Every letter of every script stays (PERÒ, D'ANGELÒ, MÜLLER-LÜDENSCHEIDT, DE LA
 * FUENTE): an accent is part of the name, never OCR dirt (OPS-1).
 */
function cleanName(s: string): string {
  return s
    .replace(/[^\p{L}\s'’-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()
}

/**
 * The licence's country, from its own title words (OPS-1). Only titles that
 * name one country are read — "PATENTE DI GUIDA" is Italy's alone, where
 * "PERMIS DE CONDUIRE" is also Belgium's, Luxembourg's and Switzerland's — so
 * the number-format check that keys on it never flags a neighbour's licence
 * against the wrong shape. Unread, the check does not run.
 */
const COUNTRY_TITLES: readonly (readonly [
  country: string,
  allOf: readonly RegExp[],
  noneOf: readonly RegExp[],
])[] = [
  ['IT', [/PATENTE DI GUIDA/], []],
  ['ES', [/PERMISO DE CONDUCCI/], []],
  ['FR', [/PERMIS DE CONDUIRE/, /R[ÉE]PUBLIQUE FRAN[ÇC]AISE/], []],
  ['GB', [/DRIVING LICENCE/, /UNITED KINGDOM|DVLA/], [/CEAD[ÚU]NAS/]],
]

export function licenceCountry(input: MrzInput): string | undefined {
  const text = (
    typeof input === 'string'
      ? input
      : input.map((l) => (typeof l === 'string' ? l : l.text)).join('\n')
  ).toUpperCase()
  return COUNTRY_TITLES.find(
    ([, allOf, noneOf]) =>
      allOf.every((re) => re.test(text)) && !noneOf.some((re) => re.test(text)),
  )?.[0]
}

/**
 * Parse an EU licence front. Returns only the fields it actually found; the
 * caller merges this with any MRZ read and the operator fills the rest. A
 * licence carries no CaRGOS document TYPE (that is the ID card or passport);
 * it contributes the licence number, its dates, and corroborating name/birth.
 */
export function parseEuLicence(input: MrzInput): ScannedIdentity {
  const f = markedFields(input)
  const identity: Record<string, unknown> = {}

  const surname = f.get('1')
  if (surname && cleanName(surname.value)) {
    identity.surname = field(cleanName(surname.value), surname.confidence * 0.9)
  }
  const given = f.get('2')
  if (given && cleanName(given.value)) {
    identity.givenNames = field(cleanName(given.value), given.confidence * 0.9)
  }

  const country = licenceCountry(input)
  if (country) identity.issuingCountry = field(country, 0.8)

  const birth = f.get('3')
  if (birth) {
    const iso = parseEuDate(birth.value, 'behind')
    if (iso) identity.birthDate = field(iso, birth.confidence * 0.85)
    // Whatever is left of field 3 once the date is removed is the birth place.
    const place = cleanName(birth.value.replace(DATE_RE, ' '))
    if (place) identity.birthPlace = field(place, birth.confidence * 0.7)
  }

  const issued = f.get('4a')
  if (issued) {
    const iso = parseEuDate(issued.value, 'behind')
    if (iso) identity.licenceIssued = field(iso, issued.confidence * 0.85)
  }
  const expiry = f.get('4b')
  if (expiry) {
    const iso = parseEuDate(expiry.value, 'ahead')
    if (iso) identity.licenceExpiry = field(iso, expiry.confidence * 0.85)
  }
  const authority = f.get('4c')
  if (authority?.value.trim()) {
    identity.licenceAuthority = field(authority.value.trim(), authority.confidence * 0.6)
  }

  const number = f.get('5')
  if (number) {
    const clean = number.value.replace(/\s+/g, '').toUpperCase()
    if (clean) identity.licenceNumber = field(clean, number.confidence * 0.8)
  }

  const categories = f.get('9')
  if (categories) {
    const cats = categories.value
      .toUpperCase()
      .split(/[\s,./]+/)
      .map((c) => c.trim())
      .filter((c) => /^(AM|A1|A2|B1|BE|C1|CE|D1|DE|[ABCDT])$/.test(c))
    if (cats.length > 0) {
      identity.licenceCategories = field(cats as readonly string[], categories.confidence * 0.75)
    }
  }

  return identity as ScannedIdentity
}
